import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, Table, TableHead, TableBody, TableRow,
  TableCell, FormControl, InputLabel, Select, MenuItem, Alert, Grid
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add, PlayArrow, Check } from '@mui/icons-material';
import { useTomaInventarioStore } from '../store/tomaStore';
import { tomaInventarioService } from '../services/tomaService';
import { type TomaInventario, type TomaInventarioDetalle, initialTomaState } from '../types/toma.types';
import { almacenService } from '../../almacenes/services/almacenService';
import type { Deposito } from '../../almacenes/types/almacen.types';

const estadoLabels: Record<string, { label: string; color: 'default' | 'info' | 'warning' | 'success' }> = {
  PEN: { label: 'Pendiente', color: 'default' },
  PRO: { label: 'En Proceso', color: 'info' },
  COM: { label: 'Completado', color: 'warning' },
  AJU: { label: 'Ajustado', color: 'success' },
};

const TomaInventarioPage = () => {
  const { tomas, loading, setTomas, setLoading } = useTomaInventarioStore();
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [openNew, setOpenNew] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedToma, setSelectedToma] = useState<TomaInventario | null>(null);
  const [formData, setFormData] = useState(initialTomaState);
  const [editedDetalles, setEditedDetalles] = useState<TomaInventarioDetalle[]>([]);

  useEffect(() => { loadData(); loadDepositos(); }, []);

  const loadDepositos = async () => {
    try { setDepositos(await almacenService.getAll()); } catch { /* handled */ }
  };

  const loadData = async () => {
    setLoading(true);
    try { setTomas(await tomaInventarioService.getAll()); } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await tomaInventarioService.create(formData);
      setOpenNew(false);
      setFormData(initialTomaState);
      await loadData();
    } catch { /* handled */ }
  };

  const handleOpenDetail = async (id: number) => {
    try {
      const toma = await tomaInventarioService.getById(id);
      setSelectedToma(toma);
      setEditedDetalles(toma.detalles || []);
      setOpenDetail(true);
    } catch { /* handled */ }
  };

  const handleUpdateCantidadFisica = (idDetalle: number, cantidad: number | null) => {
    setEditedDetalles(prev => prev.map(d => {
      if (d.idTomaInventarioDetalle === idDetalle) {
        const diff = cantidad !== null ? cantidad - d.cantidadSistema : null;
        return { ...d, cantidadFisica: cantidad, diferencia: diff };
      }
      return d;
    }));
  };

  const handleSaveDetalles = async () => {
    if (!selectedToma) return;
    try {
      await tomaInventarioService.updateDetalles(selectedToma.idTomaInventario, editedDetalles);
      alert('Cantidades físicas guardadas');
    } catch { /* handled */ }
  };

  const handleGenerarAjuste = async () => {
    if (!selectedToma) return;
    if (!confirm('¿Generar movimiento de ajuste? Esto creará entradas/salidas para igualar el stock.')) return;
    try {
      await tomaInventarioService.generarAjuste(selectedToma.idTomaInventario);
      setOpenDetail(false);
      await loadData();
      alert('Ajuste generado correctamente');
    } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'idTomaInventario', headerName: 'ID', width: 70 },
    { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 200 },
    { field: 'fecha', headerName: 'Fecha', width: 120,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '' },
    { field: 'depositoNombre', headerName: 'Almacén', width: 150 },
    { field: 'estado', headerName: 'Estado', width: 130,
      renderCell: (params) => {
        const info = estadoLabels[params.value] || { label: params.value, color: 'default' as const };
        return <Chip label={info.label} size="small" color={info.color} />;
      }
    },
    { field: 'actions', headerName: 'Acciones', width: 150, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" startIcon={<PlayArrow />} onClick={() => handleOpenDetail(params.row.idTomaInventario)}>
            {params.row.estado === 'AJU' ? 'Ver' : 'Procesar'}
          </Button>
        </Box>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Toma de Inventario</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenNew(true)}>Nueva Toma</Button>
      </Box>

      <Paper sx={{ height: 500 }}>
        <DataGrid rows={tomas} columns={columns} getRowId={(row) => row.idTomaInventario}
          loading={loading} density="compact"
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Paper>

      {/* New Toma Dialog */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Toma de Inventario</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Nombre" value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Almacén</InputLabel>
                <Select value={formData.idDeposito ?? ''} label="Almacén"
                  onChange={(e) => setFormData({ ...formData, idDeposito: Number(e.target.value) })}>
                  {depositos.map((d) => <MenuItem key={d.idDeposito} value={d.idDeposito}>{d.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Observaciones" multiline rows={2}
                value={formData.observaciones ?? ''}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            Al crear la toma, se cargarán automáticamente todos los artículos con stock en el almacén seleccionado.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>Crear Toma</Button>
        </DialogActions>
      </Dialog>

      {/* Detail / Process Dialog */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Toma de Inventario: {selectedToma?.nombre}
          {selectedToma && (
            <Chip label={estadoLabels[selectedToma.estado]?.label || selectedToma.estado}
              color={estadoLabels[selectedToma.estado]?.color || 'default'} size="small" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedToma && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Almacén: {selectedToma.depositoNombre} | Fecha: {new Date(selectedToma.fecha).toLocaleDateString()}
                </Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Cant. Sistema</TableCell>
                    <TableCell align="right">Cant. Física</TableCell>
                    <TableCell align="right">Diferencia</TableCell>
                    <TableCell>Obs.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editedDetalles.map((d) => (
                    <TableRow key={d.idTomaInventarioDetalle}
                      sx={{ bgcolor: d.diferencia && d.diferencia !== 0 ? (d.diferencia > 0 ? 'success.light' : 'error.light') : undefined }}>
                      <TableCell>{d.articuloCodigo}</TableCell>
                      <TableCell>{d.articuloDescripcion}</TableCell>
                      <TableCell align="right">{d.cantidadSistema}</TableCell>
                      <TableCell align="right">
                        {selectedToma.estado !== 'AJU' ? (
                          <TextField size="small" type="number" value={d.cantidadFisica ?? ''}
                            onChange={(e) => handleUpdateCantidadFisica(d.idTomaInventarioDetalle, e.target.value ? Number(e.target.value) : null)}
                            sx={{ width: 100 }} />
                        ) : d.cantidadFisica ?? '-'}
                      </TableCell>
                      <TableCell align="right">
                        {d.diferencia != null ? (
                          <Chip label={d.diferencia > 0 ? `+${d.diferencia}` : d.diferencia}
                            size="small" color={d.diferencia === 0 ? 'default' : d.diferencia > 0 ? 'success' : 'error'} />
                        ) : '-'}
                      </TableCell>
                      <TableCell>{d.observaciones}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetail(false)}>Cerrar</Button>
          {selectedToma?.estado !== 'AJU' && (
            <>
              <Button variant="outlined" onClick={handleSaveDetalles}>Guardar Cantidades</Button>
              <Button variant="contained" color="warning" startIcon={<Check />} onClick={handleGenerarAjuste}>
                Generar Ajuste
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TomaInventarioPage;
